import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaFire, FaUsers, FaUserPlus, FaHeart, FaHashtag, FaComment, FaShare, FaGlobe } from 'react-icons/fa';
import { discoverAPI, hashtagsAPI, usersAPI, postsAPI } from '../utils/api';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import VerificationBadge from '../components/VerificationBadge';
import { useNavigate, Link } from 'react-router-dom';

const Explore = () => {
  const [activeTab, setActiveTab] = useState('trending');
  const [trendingPosts, setTrendingPosts] = useState([]);
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [trendingHashtags, setTrendingHashtags] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadExploreData();
  }, []);

  const loadExploreData = async () => {
    try {
      const [posts, users, hashtags] = await Promise.all([
        discoverAPI.getPosts(),
        discoverAPI.getSuggestedUsers(),
        hashtagsAPI.getTrending(10)
      ]);
      setTrendingPosts(posts.data || []);
      setSuggestedUsers(users.data || []);
      setTrendingHashtags(hashtags.data || []);
    } catch (error) {
      console.error('Failed to load explore data', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async (userId) => {
    try {
      await usersAPI.follow(userId);
      setSuggestedUsers(suggestedUsers.map(u => 
        u.id === userId ? { ...u, isFollowing: true } : u
      ));
      toast.success('User followed!');
    } catch (error) {
      toast.error('Failed to follow user');
    }
  };

  const handleLike = async (postId) => {
    try {
      await postsAPI.like(postId);
      setTrendingPosts(trendingPosts.map(p => 
        p.id === postId ? { ...p, likes_count: p.likes_count + 1 } : p
      ));
    } catch (error) {
      toast.error('Failed to like post');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="grid grid-cols-1 md:grid-cols-12">
        <Sidebar />
        
        <main className="col-span-12 md:col-span-10 p-6 md:p-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-outfit font-bold text-gray-900">Explore</h1>
                <p className="text-gray-500 mt-1">Discover trending content and creators</p>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex space-x-2 mb-8 overflow-x-auto">
              {[
                { id: 'trending', label: 'Trending', icon: FaFire },
                { id: 'hashtags', label: 'Hashtags', icon: FaHashtag },
                { id: 'users', label: 'People', icon: FaUsers },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap ${
                    activeTab === id
                      ? 'bg-gradient-to-r from-[#4A90E2] to-[#A855F7] text-white shadow-lg'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  <Icon />
                  <span>{label}</span>
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-[#4A90E2] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {/* Trending Posts Tab */}
                {activeTab === 'trending' && (
                  <div className="space-y-6">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                      {[
                        { label: 'Trending Points', value: trendingPosts.length, icon: FaFire, color: 'orange' },
                        { label: 'Hot Hashtags', value: trendingHashtags.length, icon: FaHashtag, color: 'blue' },
                        { label: 'Active Creators', value: suggestedUsers.length, icon: FaUsers, color: 'purple' },
                        { label: 'Global Reach', value: '10K+', icon: FaGlobe, color: 'green' },
                      ].map(({ label, value, icon: Icon, color }) => (
                        <div key={label} className={`bg-white rounded-xl p-4 border border-gray-200`}>
                          <div className={`w-10 h-10 rounded-lg bg-${color}-100 flex items-center justify-center mb-3`}>
                            <Icon className={`text-${color}-500`} />
                          </div>
                          <p className="text-2xl font-bold text-gray-900">{value}</p>
                          <p className={`text-gray-500 text-sm ${label.includes('Point') ? 'font-arthaus' : ''}`}>{label}</p>
                        </div>
                      ))}
                    </div>

                    {/* Posts Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {trendingPosts.length > 0 ? trendingPosts.map((post, index) => (
                        <motion.div
                          key={post.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all"
                        >
                          {/* Post Header */}
                          <div className="flex items-center space-x-3 mb-4">
                            <Link to={`/profile/${post.user_id}`}>
                              <img
                                src={post.avatar_url || `https://ui-avatars.com/api/?name=${post.full_name}`}
                                alt={post.full_name}
                                className="w-12 h-12 rounded-full object-cover"
                              />
                            </Link>
                            <div className="flex-1">
                              <div className="flex items-center space-x-1">
                                <Link to={`/profile/${post.user_id}`} className="text-gray-900 font-semibold hover:text-[#4A90E2]">
                                  {post.full_name}
                                </Link>
                                {post.is_verified && (
                                  <VerificationBadge badgeType={post.verification_badge} size="sm" />
                                )}
                              </div>
                              <p className="text-gray-500 text-sm">@{post.username}</p>
                            </div>
                            <div className="flex items-center space-x-1 text-orange-500 bg-orange-50 px-2 py-1 rounded-full">
                              <FaFire className="text-sm" />
                              <span className="text-xs font-medium">Trending</span>
                            </div>
                          </div>

                          {/* Content */}
                          <p className="text-gray-700 mb-4 line-clamp-3">{post.content}</p>

                          {/* Media */}
                          {post.media_url && (
                            <img 
                              src={post.media_url} 
                              alt="Post" 
                              className="w-full h-48 object-cover rounded-lg mb-4" 
                            />
                          )}

                          {/* Actions */}
                          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                            <button 
                              onClick={() => handleLike(post.id)}
                              className="flex items-center space-x-2 text-gray-500 hover:text-red-500 transition-colors"
                            >
                              <FaHeart />
                              <span>{post.likes_count}</span>
                            </button>
                            <button className="flex items-center space-x-2 text-gray-500 hover:text-[#4A90E2] transition-colors">
                              <FaComment />
                              <span>{post.comments_count}</span>
                            </button>
                            <button className="flex items-center space-x-2 text-gray-500 hover:text-green-500 transition-colors">
                              <FaShare />
                              <span>{post.shares_count}</span>
                            </button>
                          </div>
                        </motion.div>
                      )) : (
                        <div className="col-span-2 text-center py-12 bg-white rounded-xl border border-gray-200">
                          <FaFire className="text-4xl text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500">No trending posts yet. Be the first to go viral!</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Hashtags Tab */}
                {activeTab === 'hashtags' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {trendingHashtags.length > 0 ? trendingHashtags.map((hashtag, index) => (
                        <motion.div
                          key={hashtag.tag}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          onClick={() => navigate(`/search?q=${encodeURIComponent(hashtag.tag)}`)}
                          className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer group"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#4A90E2] to-[#A855F7] flex items-center justify-center">
                                <FaHashtag className="text-white text-xl" />
                              </div>
                              <div>
                                <p className="text-lg font-bold text-gray-900 group-hover:text-[#4A90E2] transition-colors">
                                  {hashtag.tag}
                                </p>
                                <p className="text-gray-500 text-sm">{hashtag.post_count} posts</p>
                              </div>
                            </div>
                            {index < 3 && (
                              <div className="flex items-center space-x-1 text-orange-500 bg-orange-50 px-2 py-1 rounded-full">
                                <FaFire className="text-xs" />
                                <span className="text-xs font-medium">Hot</span>
                              </div>
                            )}
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-[#4A90E2] to-[#A855F7] rounded-full"
                              style={{ width: `${Math.min(hashtag.trending_score, 100)}%` }}
                            />
                          </div>
                        </motion.div>
                      )) : (
                        <div className="col-span-3 text-center py-12 bg-white rounded-xl border border-gray-200">
                          <FaHashtag className="text-4xl text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500">No trending hashtags yet. Start a trend!</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* People Tab */}
                {activeTab === 'users' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {suggestedUsers.length > 0 ? suggestedUsers.map((user, index) => (
                      <motion.div
                        key={user.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all"
                      >
                        {/* Cover Photo */}
                        <div 
                          className="h-24 bg-gradient-to-r from-[#4A90E2] to-[#A855F7]"
                          style={user.cover_photo_url ? { backgroundImage: `url(${user.cover_photo_url})`, backgroundSize: 'cover' } : {}}
                        />
                        
                        {/* Profile Content */}
                        <div className="px-6 pb-6 -mt-10">
                          <Link to={`/profile/${user.id}`}>
                            <img
                              src={user.avatar_url || `https://ui-avatars.com/api/?name=${user.full_name}`}
                              alt={user.full_name}
                              className="w-20 h-20 rounded-full object-cover border-4 border-white mb-3"
                            />
                          </Link>
                          <div className="flex items-center space-x-1 mb-1">
                            <Link to={`/profile/${user.id}`} className="text-lg font-bold text-gray-900 hover:text-[#4A90E2]">
                              {user.full_name}
                            </Link>
                            {user.is_verified && (
                              <VerificationBadge badgeType={user.verification_badge} size="sm" />
                            )}
                          </div>
                          <p className="text-gray-500 text-sm mb-3">@{user.username}</p>
                          {user.bio && (
                            <p className="text-gray-600 text-sm mb-4 line-clamp-2">{user.bio}</p>
                          )}
                          
                          {/* Stats */}
                          <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                            <span><strong className="text-gray-900">{user.followers_count || 0}</strong> followers</span>
                            <span><strong className="text-gray-900">{user.posts_count || 0}</strong> posts</span>
                          </div>

                          {/* Follow Button */}
                          <button
                            onClick={() => handleFollow(user.id)}
                            disabled={user.isFollowing}
                            className={`w-full py-2.5 rounded-lg font-medium transition-all flex items-center justify-center space-x-2 ${
                              user.isFollowing
                                ? 'bg-gray-100 text-gray-500'
                                : 'bg-gradient-to-r from-[#4A90E2] to-[#A855F7] text-white hover:shadow-lg'
                            }`}
                          >
                            <FaUserPlus />
                            <span>{user.isFollowing ? 'Following' : 'Follow'}</span>
                          </button>
                        </div>
                      </motion.div>
                    )) : (
                      <div className="col-span-3 text-center py-12 bg-white rounded-xl border border-gray-200">
                        <FaUsers className="text-4xl text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">No suggested users right now. Invite your friends!</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default Explore;
